import Head from 'next/head';
import { Box } from '@mui/material';
import { withRouter, type NextRouter } from 'next/router';
import type { Document } from '@contentful/rich-text-types';
import { useMount } from 'react-use';
import { getWindow, trackNavigatedToPage } from '@surfline/web-common';

import TravelGraphs from 'components/TravelGraphs';
import TravelInterview from 'components/TravelInterview';
import TravelHero from 'components/TravelHero';
import SurfZonePlaylist from 'components/SurfZonePlaylist';
import TravelEssentials from 'components/TravelEssentials';
import TravelZonePlaylist from 'components/TravelZonePlaylist';
import TravelQuickTips from 'components/TravelQuickTips';
import TravelWhenToScore from 'components/TravelWhenToScore';
import TravelLocalKnowledge from 'components/TravelLocalKnowledge';
import TravelNavbar from 'components/TravelNavbar';
import TravelLinks from 'components/TravelLinks';
import PremiumTravelCTA from 'components/PremiumTravelCTA';
import config from 'config';
import { useAppSelector } from 'stores/hooks';
import { getEntitlements } from 'selectors/user';
import { isPremiumUser, isPremiumPlusUser, getIsPremiumWithAdsUser } from 'utils/user';
import type {
  ColorPalette,
  SurfSpot,
  TravelInterview as TravelInterviewReponse,
  TravelPlaylistItem,
} from 'types/contentful/content-types';
import type { ContentfulMediaField } from 'types/contentful/fields';
import getTravelConditions from 'utils/getTravelConditions';

import Styles from './TravelZone.module.scss';

interface Hero {
  title: string;
  subtitle: string;
  isVideoHero?: boolean;
  centerTextVertially?: boolean;
  showScrollArrow?: boolean;
  heroPoster?: ContentfulMediaField | undefined;
  heroVideoDesktop?: ContentfulMediaField | undefined;
  heroVideoMobile?: ContentfulMediaField | undefined;
  date?: Date | string;
  link?: string;
}

export interface TravelZoneResponse {
  sys: {
    id: string;
  };
  title?: string;
  slug?: string;
  travelRegion?: {
    title?: string;
  };
  colorPalette: ColorPalette;
  hero?: Hero;
  surfSpotsCollection: {
    items: SurfSpot[];
  };
  travelEssentialsTitle: string;
  travelEssentialsBody: {
    json: Document;
    links: {
      assets: {
        block: {
          fileName: string;
          title: string;
          description: string;
          url: string;
          sys: {
            id: string;
          };
        }[];
      };
    };
  };
  travelInterview: TravelInterviewReponse;
  travelEssentials: {
    title: string;
    howToGetThere: string;
    whatToBring: string;
    downtime: string;
    localScene: string;
  };
  travelZonePlaylist: {
    itemAspectRatio?: number;
    playlistColumns?: number;
    showPageCount?: boolean;
    title?: string;
    zonesCollection: {
      items: TravelPlaylistItem[];
    };
  };
  quickTips: {
    travelTimes: string;
    connectivity: string;
    currency: string;
    averageCost: string;
    visaRequirements: string;
    drinkingWaterQuality: string;
    hazards: string;
    cashCardCrypto: string;
  };
  whenToScore: {
    forecaster: {
      name: string;
      role: string;
      avatar: {
        url: string;
      };
    };
    highSeasonMonths: string;
    highMonths: string[];
    highSeasonAbility: string;
    highSeasonCrowd: string;
    highSeasonDescription: {
      json: Document;
    };
    shoulderSeasonMonths: string;
    shoulderMonths: string[];
    shoulderSeasonAbility: string;
    shoulderSeasonCrowd: string;
    shoulderSeasonDescription: {
      json: Document;
    };
    lowSeasonMonths: string;
    lowMonths: string[];
    lowSeasonAbility: string;
    lowSeasonCrowd: string;
    lowSeasonDescription: {
      json: Document;
    };
  };
  localKnowledge: {
    alignImage: string;
    heroImage: {
      title: string;
      contentType: string;
      url: string;
    };
    title: string;
    localExpert: string;
    subtitle: string;
    questionsAndAnswers: {
      json: JSON;
    };
  };
}

interface TravelZoneProps {
  router: NextRouter;
  page: {
    travelZone: TravelZoneResponse;
    payloadStatus: number;
  };
}

const TravelZone: React.FC<TravelZoneProps> = ({ router, page }) => {
  const { asPath, query } = router;
  const { slug, id, spot } = query;

  const entitlements = useAppSelector(getEntitlements);
  const isPremium = isPremiumUser(entitlements);
  const isPremiumPlus = isPremiumPlusUser(entitlements);
  const isPremiumWithAds = getIsPremiumWithAdsUser(entitlements);
  const showWhenToScoreSection = isPremiumWithAds || isPremium || isPremiumPlus;

  const zone = page?.travelZone;
  const colorPalette = zone?.colorPalette;
  const hero = zone?.hero;
  const interview = zone?.travelInterview;
  const travelZonePlaylist = zone?.travelZonePlaylist;
  const quickTips = zone?.quickTips;
  const surfSpotsCollection = zone?.surfSpotsCollection;

  const travelEssentials = {
    title: zone?.travelEssentialsTitle,
    body: zone?.travelEssentialsBody,
  };

  const whenToScore = zone?.whenToScore;
  const localKnowledge = zone?.localKnowledge;

  useMount(() => {
    trackNavigatedToPage('Page Viewed', {
      category: 'travel',
      path: asPath,
      referrer: `${getWindow()?.location?.href ?? ''}${asPath}`,
      search: getWindow().location.search,
      title: `${zone?.title} Travel Guide`,
      url: `${getWindow()?.location?.href ?? ''}${asPath}`,
    });
  });

  return (
    <>
      <Head>
        <title>{config.meta.title.travel.zone(zone?.title ?? '')}</title>
        <meta
          property="og:image"
          content={config.cloudflareImageResizingUrl(
            hero?.heroPoster?.url ?? config.defaultImageUrl,
            'h=630,w=1200,q=85,f=auto,fit=cover',
          )}
        />
      </Head>
      <Box className={Styles.main} data-slug={slug} data-zone-id={id}>
        <TravelNavbar />
        <TravelHero
          data={{
            centerTextVertically: hero?.centerTextVertially,
            heroPoster: hero?.heroPoster,
            heroVideoDesktop: hero?.heroVideoDesktop,
            heroVideoMobile: hero?.heroVideoMobile,
            isVideoHero: hero?.isVideoHero,
            showScrollArrow: hero?.showScrollArrow,
            subtitle: hero?.subtitle,
            title: hero?.title || `Travel Zone`,
          }}
        />
        <SurfZonePlaylist
          data={surfSpotsCollection}
          pageTitle={config.meta.title.travel.zone(zone?.title ?? '')}
          spot={spot?.toString()}
          zoneSlug={zone?.slug ?? ''}
        />
        <TravelInterview
          attribution={interview?.attribution}
          colorPalette={{
            primary: `#${colorPalette?.primaryColor}`,
            secondary: `#${colorPalette?.secondaryColor}`,
            accent: `#${colorPalette?.accentColor}`,
          }}
          displayAds={interview?.displayAds ?? false}
          excerpt={interview?.excerpt?.json}
          imageUrl={interview?.image?.url}
          playerId={interview?.playerId}
          videoId={interview?.videoId}
          videoPosterUrl={interview?.videoPoster?.url}
          videoCta={interview?.videoCta}
        />

        {showWhenToScoreSection ? (
          <>
            <TravelWhenToScore
              forecaster={whenToScore?.forecaster}
              highSeasonMonths={whenToScore?.highSeasonMonths}
              highSeasonAbility={whenToScore?.highSeasonAbility}
              highSeasonCrowd={whenToScore?.highSeasonCrowd}
              highSeasonDescription={whenToScore?.highSeasonDescription}
              shoulderSeasonMonths={whenToScore?.shoulderSeasonMonths}
              shoulderSeasonAbility={whenToScore?.shoulderSeasonAbility}
              shoulderSeasonCrowd={whenToScore?.shoulderSeasonCrowd}
              shoulderSeasonDescription={whenToScore?.shoulderSeasonDescription}
              lowSeasonMonths={whenToScore?.lowSeasonMonths}
              lowSeasonAbility={whenToScore?.lowSeasonAbility}
              lowSeasonCrowd={whenToScore?.lowSeasonCrowd}
              lowSeasonDescription={whenToScore?.lowSeasonDescription}
            />
            <TravelGraphs
              zoneId={zone?.sys?.id}
              conditions={getTravelConditions(whenToScore)}
              isError={!zone?.sys?.id}
            />
          </>
        ) : (
          <PremiumTravelCTA />
        )}
        <TravelLocalKnowledge
          alignImage={localKnowledge?.alignImage}
          heroImage={localKnowledge?.heroImage}
          localExpert={localKnowledge?.localExpert}
          subtitle={localKnowledge?.subtitle}
          colorPalette={zone?.colorPalette}
          title={localKnowledge?.title}
          questionsAndAnswers={localKnowledge?.questionsAndAnswers}
        />
        <TravelEssentials title={travelEssentials?.title} body={travelEssentials?.body} />
        <TravelQuickTips
          averageCost={quickTips?.averageCost}
          cashCardCrypto={quickTips?.cashCardCrypto}
          colorPalette={{
            primary: `#${colorPalette?.primaryColor}`,
            secondary: `#${colorPalette?.secondaryColor}`,
            accent: `#${colorPalette?.accentColor}`,
          }}
          connectivity={quickTips?.connectivity}
          currency={quickTips?.currency}
          drinkingWaterQuality={quickTips?.drinkingWaterQuality}
          hazards={quickTips?.hazards}
          travelTimes={quickTips?.travelTimes}
          visaRequirements={quickTips?.visaRequirements}
        />
        <TravelZonePlaylist data={travelZonePlaylist} />
        <TravelLinks />
      </Box>
    </>
  );
};

export default withRouter(TravelZone);
